import { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { toast } from 'react-toastify';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { toCanvas } from 'html-to-image';
import { jsPDF } from 'jspdf';
import type { ClientReportSnapshot } from '../../utils/client-reports/types';
import ReportDocument from './ReportDocument';
import { ReportLayoutContext } from './reportLayout';

// Arma el PDF de un reporte con el mismo dibujo del link: se renderiza el
// reporte fuera de pantalla en ancho de computador, se captura por bloques
// (encabezado y cada bloque de las secciones) y se reparten en hojas A4 sin
// cortar un bloque a la mitad, salvo que no quepa en una hoja entera.

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const MARGIN_MM = 12;
const FOOTER_MM = 8;
const GAP_MM = 4;
const RENDER_WIDTH_PX = 1040;
const RENDER_PADDING_PX = 24;

const waitForImages = (root: HTMLElement) =>
  Promise.all(
    Array.from(root.querySelectorAll('img')).map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
    )
  );

// Encabezado del reporte y, dentro de cada sección, cada hijo directo del
// <section> (título, indicadores, gráficos, tabla) como un bloque. El título
// de la sección no se deja solo al final de una hoja.
const collectBlocks = (root: HTMLElement) => {
  const blocks: Array<{ el: HTMLElement; keepWithNext: boolean }> = [];
  const header = root.querySelector<HTMLElement>('[data-pdf-block]');
  if (header) blocks.push({ el: header, keepWithNext: false });
  root.querySelectorAll<HTMLElement>('[data-pdf-section] > section').forEach((section) => {
    Array.from(section.children).forEach((child, index) =>
      blocks.push({ el: child as HTMLElement, keepWithNext: index === 0 })
    );
  });
  return blocks;
};

export const reportPdfFileName = (snapshot: ClientReportSnapshot) => {
  const who =
    snapshot.receivers.length === 1
      ? snapshot.receivers[0]
      : `${snapshot.receivers.length} clientes`;
  const day = snapshot.generatedAt.slice(0, 10);
  const name =
    snapshot.kind === 'pendingSettlements' ? 'Liquidaciones por recibir' : 'Reporte';
  return `${name} ${who} ${snapshot.speciesName} ${snapshot.season} ${day}`
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .concat('.pdf');
};

export const downloadReportPdf = async (
  snapshot: ClientReportSnapshot,
  options: { footer: string; pageLabel: (page: number, total: number) => string }
) => {
  const container = document.createElement('div');
  // Fuera de la vista pero dentro de la ventana, para que html2canvas la mida
  // bien. Tema claro siempre: el PDF se imprime sobre blanco.
  container.className = 'light bg-background text-foreground';
  Object.assign(container.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: `${RENDER_WIDTH_PX}px`,
    zIndex: '-1',
    pointerEvents: 'none',
  });
  document.body.appendChild(container);
  const root = createRoot(container);

  try {
    root.render(
      <ReportLayoutContext.Provider value='pdf'>
        <div style={{ padding: RENDER_PADDING_PX }}>
          <ReportDocument snapshot={snapshot} />
        </div>
      </ReportLayoutContext.Provider>
    );
    await new Promise((resolve) => setTimeout(resolve, 400));
    await waitForImages(container);
    if (document.fonts?.ready) await document.fonts.ready;

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const contentWidth = PAGE_WIDTH_MM - MARGIN_MM * 2;
    const mmPerPx = contentWidth / (RENDER_WIDTH_PX - RENDER_PADDING_PX * 2);
    const pageBottom = PAGE_HEIGHT_MM - MARGIN_MM - FOOTER_MM;
    let cursor = MARGIN_MM;

    const blocks = collectBlocks(container);
    for (const [index, { el: block, keepWithNext }] of blocks.entries()) {
      // html-to-image dibuja con el motor del navegador, así el texto queda
      // igual que en pantalla (html2canvas lo corre unos px y lo corta).
      const canvas = await toCanvas(block, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
      });
      if (!canvas.width || !canvas.height) continue;
      // El ancho útil del render (sin el padding) ocupa el ancho útil de la hoja.
      const blockWidthMm = block.offsetWidth * mmPerPx;
      const scale = blockWidthMm / canvas.width;
      const blockHeightMm = canvas.height * scale;

      const nextHeightMm =
        keepWithNext && blocks[index + 1]
          ? Math.min(blocks[index + 1].el.offsetHeight * mmPerPx, 60) + GAP_MM
          : 0;
      if (cursor > MARGIN_MM && cursor + blockHeightMm + nextHeightMm > pageBottom) {
        pdf.addPage();
        cursor = MARGIN_MM;
      }

      if (blockHeightMm <= pageBottom - cursor) {
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', MARGIN_MM, cursor, blockWidthMm, blockHeightMm);
        cursor += blockHeightMm + GAP_MM;
        continue;
      }

      // Bloque más alto que una hoja (ej. una tabla larga): se corta en tiras.
      let offsetPx = 0;
      while (offsetPx < canvas.height) {
        const availableMm = pageBottom - cursor;
        const slicePx = Math.min(canvas.height - offsetPx, Math.floor(availableMm / scale));
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = slicePx;
        slice.getContext('2d')!.drawImage(canvas, 0, offsetPx, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
        pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', MARGIN_MM, cursor, blockWidthMm, slicePx * scale);
        offsetPx += slicePx;
        cursor += slicePx * scale + GAP_MM;
        if (offsetPx < canvas.height) {
          pdf.addPage();
          cursor = MARGIN_MM;
        }
      }
    }

    const total = pdf.getNumberOfPages();
    for (let page = 1; page <= total; page += 1) {
      pdf.setPage(page);
      pdf.setFontSize(8);
      pdf.setTextColor(150);
      pdf.text(options.footer, MARGIN_MM, PAGE_HEIGHT_MM - MARGIN_MM / 2);
      pdf.text(options.pageLabel(page, total), PAGE_WIDTH_MM - MARGIN_MM, PAGE_HEIGHT_MM - MARGIN_MM / 2, {
        align: 'right',
      });
    }

    pdf.save(reportPdfFileName(snapshot));
  } finally {
    root.unmount();
    container.remove();
  }
};

// Descarga con los textos traducidos y el aviso de error. Devuelve la función
// y si hay una descarga en curso, para el estado del botón.
export const useDownloadReportPdf = () => {
  const { t } = useTranslation('reports');
  const [downloading, setDownloading] = useState(false);

  const download = async (snapshots: ClientReportSnapshot[]) => {
    setDownloading(true);
    try {
      for (const snapshot of snapshots) {
        await downloadReportPdf(snapshot, {
          footer: t('Generado con {{brand}}', { brand: 'FruitMarkets' }),
          pageLabel: (page, total) => t('Página {{page}} de {{total}}', { page, total }),
        });
      }
    } catch (error) {
      console.error('Error generating report PDF:', error);
      toast.error(t('No se pudo generar el PDF'));
    } finally {
      setDownloading(false);
    }
  };

  return { download, downloading };
};
